import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AlertTriangle, Plus } from 'lucide-react';
import { client, errorMessage } from '../../api/client';
import type { CustomField, Page } from '../../types';
import { useAuth } from '../../store/auth';

/**
 * Fields a super admin adds to the candidate and employee forms.
 *
 * Per company, not global — a field defined here appears on the forms of the
 * company selected in the picker above, and nowhere else. That is why the page
 * says so rather than leaving it to be discovered.
 *
 * Nothing is deleted. Deactivating stops a field being offered and leaves
 * every value already recorded against it readable, which is the difference
 * between retiring a question and pretending it was never asked.
 */
const TYPES = [
  { value: 'text', label: 'Text' },
  { value: 'dropdown', label: 'Dropdown' },
  { value: 'date', label: 'Date' },
  { value: 'number', label: 'Number' },
] as const;

const ENTITIES = [
  { value: 'candidate', label: 'Candidate form' },
  { value: 'employee', label: 'Employee form' },
] as const;

type Draft = {
  entity: 'candidate' | 'employee';
  label: string;
  fieldType: 'text' | 'dropdown' | 'date' | 'number';
  optionsText: string;
  isRequired: boolean;
  includeInExport: boolean;
};

const EMPTY: Draft = {
  entity: 'candidate',
  label: '',
  fieldType: 'text',
  optionsText: '',
  isRequired: false,
  includeInExport: false,
};

export default function CustomFieldsPage() {
  const queryClient = useQueryClient();
  const actingCompanyId = useAuth((s) => s.actingCompanyId);
  const [draft, setDraft] = useState<Draft>(EMPTY);
  /**
   * The sensitive-name warning the server returned, held until confirmed.
   *
   * Deliberately not a checkbox on the form. A tickbox sitting there before
   * anybody has typed anything is one people tick once and forget; a sentence
   * that appears naming the word it matched is one they read.
   */
  const [warning, setWarning] = useState<string | null>(null);

  const fields = useQuery<Page<CustomField>>({
    queryKey: ['custom-fields', 'all'],
    queryFn: async () =>
      (
        await client.get('/api/v1/custom-fields', {
          params: { includeInactive: true, limit: 200 },
        })
      ).data,
  });

  const create = useMutation({
    mutationFn: async (acknowledge: boolean) =>
      (
        await client.post('/api/v1/custom-fields', {
          entity: draft.entity,
          label: draft.label.trim(),
          fieldType: draft.fieldType,
          options:
            draft.fieldType === 'dropdown'
              ? draft.optionsText
                  .split('\n')
                  .map((o) => o.trim())
                  .filter(Boolean)
              : null,
          isRequired: draft.isRequired,
          includeInExport: draft.includeInExport,
          acknowledgeSensitive: acknowledge,
        })
      ).data,
    onSuccess: () => {
      toast.success('Field added');
      setDraft(EMPTY);
      setWarning(null);
      void queryClient.invalidateQueries({ queryKey: ['custom-fields'] });
    },
    onError: (error) => {
      const message = errorMessage(error);
      // The server refuses a sensitive-looking name once and says why. Show
      // that sentence rather than a generic failure, because the sentence is
      // the entire point of the refusal.
      if (message.includes('sensitive personal data')) setWarning(message);
      else toast.error(message);
    },
  });

  const toggle = useMutation({
    mutationFn: async ({ id, isActive }: { id: string; isActive: boolean }) =>
      (await client.patch(`/api/v1/custom-fields/${id}`, { isActive })).data,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['custom-fields'] });
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const rows = fields.data?.items ?? [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-lg font-semibold text-gray-900">Custom Fields</h1>
        <p className="mt-1 text-sm text-gray-500">
          Extra boxes on the candidate and employee forms. They belong to the
          company selected above, and appear on that company's forms only.
        </p>
      </div>

      {!actingCompanyId && (
        <div className="card border-amber-200 bg-amber-50">
          <p className="text-sm text-amber-900">
            <strong>No company selected.</strong> Choose one from the picker at
            the top before adding a field — a field has to belong to somebody.
          </p>
        </div>
      )}

      <div className="card space-y-4">
        <h2 className="text-sm font-semibold text-gray-900">Add a Field</h2>

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="label" htmlFor="cf-entity">Appears On</label>
            <select
              id="cf-entity"
              className="input"
              value={draft.entity}
              onChange={(e) =>
                setDraft({ ...draft, entity: e.target.value as Draft['entity'] })
              }
            >
              {ENTITIES.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="label" htmlFor="cf-type">Type</label>
            <select
              id="cf-type"
              className="input"
              value={draft.fieldType}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  fieldType: e.target.value as Draft['fieldType'],
                })
              }
            >
              {TYPES.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label className="label" htmlFor="cf-label">Label</label>
          <input
            id="cf-label"
            className="input"
            spellCheck={true}
            maxLength={120}
            placeholder="Notice period, Shift preference…"
            value={draft.label}
            onChange={(e) => {
              setDraft({ ...draft, label: e.target.value });
              // A fresh name deserves a fresh judgement.
              setWarning(null);
            }}
          />
        </div>

        {draft.fieldType === 'dropdown' && (
          <div>
            <label className="label" htmlFor="cf-options">
              Options, One Per Line
            </label>
            <textarea
              id="cf-options"
              className="input min-h-24"
              spellCheck={true}
              placeholder={'Day\nNight\nRotational'}
              value={draft.optionsText}
              onChange={(e) => setDraft({ ...draft, optionsText: e.target.value })}
            />
            <p className="mt-1 text-xs text-gray-500">
              At least two, and they cannot be removed later once records hold
              them — add options freely, retire the field to stop offering it.
            </p>
          </div>
        )}

        <div className="flex flex-wrap gap-4">
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={draft.isRequired}
              onChange={(e) => setDraft({ ...draft, isRequired: e.target.checked })}
            />
            Required on new records
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={draft.includeInExport}
              onChange={(e) =>
                setDraft({ ...draft, includeInExport: e.target.checked })
              }
            />
            Include in exports
          </label>
        </div>

        {warning && (
          <div className="rounded-lg border border-amber-300 bg-amber-50 p-3">
            <div className="flex gap-2">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
              <div className="space-y-2">
                <p className="text-xs text-amber-900">{warning}</p>
                <button
                  type="button"
                  className="btn-ghost text-xs"
                  disabled={create.isPending}
                  onClick={() => create.mutate(true)}
                >
                  Add it anyway — record that I confirmed this
                </button>
              </div>
            </div>
          </div>
        )}

        <button
          type="button"
          className="btn-primary"
          disabled={
            !draft.label.trim() || !actingCompanyId || create.isPending
          }
          onClick={() => create.mutate(false)}
        >
          <Plus className="h-4 w-4" />
          Add field
        </button>
      </div>

      <div className="card overflow-x-auto p-0">
        <table className="w-full text-left text-sm">
          <thead className="border-b border-gray-200 text-xs text-gray-500">
            <tr>
              <th className="px-4 py-2.5 font-medium">Label</th>
              <th className="px-4 py-2.5 font-medium">Form</th>
              <th className="px-4 py-2.5 font-medium">Type</th>
              <th className="px-4 py-2.5 font-medium">Required</th>
              <th className="px-4 py-2.5 font-medium">In Exports</th>
              <th className="px-4 py-2.5 font-medium">Status</th>
              <th className="px-4 py-2.5 font-medium"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-gray-400">
                  No custom fields yet.
                </td>
              </tr>
            )}
            {rows.map((field) => (
              <tr key={field.id} className={field.isActive ? '' : 'text-gray-400'}>
                <td className="px-4 py-2.5 font-medium">{field.label}</td>
                <td className="px-4 py-2.5">{field.entity}</td>
                <td className="px-4 py-2.5">
                  {field.fieldType}
                  {field.options && (
                    <span className="text-xs text-gray-500">
                      {' '}({field.options.length})
                    </span>
                  )}
                </td>
                <td className="px-4 py-2.5">{field.isRequired ? 'Yes' : '—'}</td>
                <td className="px-4 py-2.5">
                  {field.includeInExport ? 'Yes' : '—'}
                </td>
                <td className="px-4 py-2.5">
                  {field.isActive ? 'Active' : 'Retired'}
                </td>
                <td className="whitespace-nowrap px-4 py-2.5">
                  <button
                    type="button"
                    className="btn-ghost text-xs"
                    disabled={toggle.isPending}
                    onClick={() =>
                      toggle.mutate({ id: field.id, isActive: !field.isActive })
                    }
                    title={
                      field.isActive
                        ? 'Stop offering this field. Values already recorded stay readable.'
                        : 'Offer this field again.'
                    }
                  >
                    {field.isActive ? 'Retire' : 'Restore'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
